import { Behavior } from '../entities/Behavior';
import { Vector2 } from '../math/Vector2';

/** How a {@link FollowPointer} chases. Plain data, like every behavior's configuration. */
export interface FollowPointerOptions {
  /** Top speed in world units per second. */
  readonly speed?: number;
  /** How close is close enough, in world units; inside this the entity stops rather than jitters. */
  readonly stopDistance?: number;
  /** Whether to also rotate so the entity's +X axis points the way it is traveling. */
  readonly faceDirection?: boolean;
  /** Whether to move only while the pointer is held down, rather than always chasing it. */
  readonly onlyWhileDown?: boolean;
}

const DEFAULT_SPEED = 600;
const DEFAULT_STOP_DISTANCE = 4;

/**
 * Moves its entity towards the pointer at a fixed speed.
 *
 * The behavior a toddler game is built on: something friendly follows the finger, at a speed slow
 * enough to be chased rather than teleported to. Movement is per second and clamped by the remaining
 * distance, so it neither overshoots on a long frame nor stutters on a short one.
 *
 * Reads `context.input`, which the input system has already settled for the frame, so every follower
 * in a scene aims at the same point.
 *
 * Nothing moves while the pointer is off the canvas, which covers both the mouse leaving the window and
 * the frames before it has ever arrived. Chasing a pointer that is not there would send the entity to
 * the canvas' top left corner, which is where an unset position converts to.
 */
export class FollowPointer extends Behavior {
  readonly #toTarget = Vector2.zero();
  readonly #speed: number;
  readonly #stopDistance: number;
  readonly #faceDirection: boolean;
  readonly #onlyWhileDown: boolean;

  constructor(options: FollowPointerOptions = {}) {
    super();
    this.#speed = options.speed ?? DEFAULT_SPEED;
    this.#stopDistance = options.stopDistance ?? DEFAULT_STOP_DISTANCE;
    this.#faceDirection = options.faceDirection ?? false;
    this.#onlyWhileDown = options.onlyWhileDown ?? false;
  }

  /** Steps one frame's worth of chase. */
  override onUpdate(deltaSeconds: number): void {
    const pointer = this.context.input.pointer;
    if (!pointer.isOverCanvas || (this.#onlyWhileDown && !pointer.isDown)) {
      return;
    }

    const position = this.entity.transform.position;
    this.#toTarget.copyFrom(pointer.worldPosition).subtract(position);

    const distance = this.#toTarget.length;
    if (distance <= this.#stopDistance) {
      return;
    }

    if (this.#faceDirection) {
      this.entity.transform.rotation = this.#toTarget.angle;
    }

    const step = Math.min(this.#speed * deltaSeconds, distance);
    position.addScaled(this.#toTarget.normalize(), step);
  }
}
